import { ApolloError } from '@apollo/client'
import { isEmpty, isUndefined, omit, pick } from 'lodash'
import { IntlShape, defineMessages } from 'react-intl'

import { DEFAULT_COUNTRY_CODE } from 'components/Formik/InputPhone/country-codes-list'
import {
  BusinessClientSuggestion,
  BusinessCustomerSuggestion,
  BusinessSiteSuggestion,
  ChangeBusinessCustomerBusinessClientInput
} from 'gql/generated/graphql'
import {
  GTAssignBusinessCustomerKreditCategoryInput,
  GTBusinessCustomer,
  GTBusinessCustomerTypeInput,
  GTBusinessCustomersInput,
  GTChangeBusinessClientCustomerKreditsAmountInput,
  GTChangeBusinessCustomerBusinessSiteInput,
  GTChangeUserAccountDetailsInput,
  GTCreateBusinessCustomerInput,
  GTCreateBusinessCustomersFromCsvInput,
  GTCreatePlaceForBusinessCustomerInput
} from 'gql/types.generated'
import { getQueryString } from 'utils/route'
import { getDifference } from 'utils/object'
import { sanitizePrice } from 'utils/math'
import {
  EBusinessCustomerStatus,
  IBusinessCustomerCSVFormValues,
  IBusinessCustomerFormValues,
  IBusinessCustomerUserAccountPDFData,
  IBusinessCustomersFiltersForm
} from './types'

const csvErrorMessages = defineMessages({
  rowError: {
    id: 'businessCustomerCSVForm.errors.rowError',
    defaultMessage: 'Error in row: {content}'
  },
  genericError: {
    id: 'businessCustomerCSVForm.errors.genericError',
    defaultMessage: 'Data in the file does not have the right format. Try again'
  },
  invalidCity: {
    id: 'businessCustomerCSVForm.errors.invalidCity',
    defaultMessage: 'El id del city es incorrecto o no existe.'
  }
})

const mapFormToUpdateMutation = (
  formValues: IBusinessCustomerFormValues,
  oldCustomer: GTBusinessCustomer
): {
  userAccount?: GTChangeUserAccountDetailsInput
  place?: GTCreatePlaceForBusinessCustomerInput
  category?: GTAssignBusinessCustomerKreditCategoryInput
  amount?: GTChangeBusinessClientCustomerKreditsAmountInput
  businessSite?: GTChangeBusinessCustomerBusinessSiteInput
  businessClient?: ChangeBusinessCustomerBusinessClientInput
} => {
  const oldValues = BusinessCustomerFormUtils.mapResponseToForm(oldCustomer)
  const payload = getDifference(formValues, oldValues)
  const kreditsSettingsPayload = getDifference(formValues.kreditsSettings, oldValues.kreditsSettings)
  const mutationInput: {
    userAccount?: GTChangeUserAccountDetailsInput
    place?: GTCreatePlaceForBusinessCustomerInput
    category?: GTAssignBusinessCustomerKreditCategoryInput
    amount?: GTChangeBusinessClientCustomerKreditsAmountInput
    businessSite?: GTChangeBusinessCustomerBusinessSiteInput
    // @ts-expect-error TODO: Fix this when the backend is ready
    businessClient?: GTChangeBusinessCustomerBusinessClientInput
  } = {
    userAccount: undefined,
    place: undefined,
    category: undefined,
    amount: undefined,
    businessSite: undefined
  }

  if (!isEmpty(pick(payload, ['email', 'firstName', 'lastName', 'phoneNumber', 'kreditsSettings']))) {
    const isNameModified = payload.firstName || payload.lastName

    mutationInput.userAccount = {
      id: formValues.userAccountId,
      email: payload.email,
      firstName: isNameModified ? formValues.firstName : undefined,
      lastName: isNameModified ? formValues.lastName : undefined,
      phoneNumber:
        payload.phoneNumber?.number || payload.phoneNumber?.countryCode
          ? {
              number: formValues.phoneNumber.number as string,
              countryCode: formValues.phoneNumber.countryCode as string
            }
          : undefined
    }
  }

  if (!isEmpty(omit(payload.placeAddress, 'cityName'))) {
    const place = payload.placeAddress as IBusinessCustomerFormValues['placeAddress']
    const firstName = (formValues.firstName || oldValues.firstName) as string
    const lastName = (formValues.lastName || oldValues.lastName) as string
    const name = `${firstName} ${lastName}`

    mutationInput.place = {
      businessCustomerID: oldCustomer.id,
      businessClientID: oldValues.businessClient.id,
      businessSiteID: oldValues.businessSite.id || null,
      name: name,
      place: {
        address: place.street,
        zipCode: place.zipCode,
        parentID: place.cityId,
        location: place.location.latitude
          ? {
              latitude: place.location.latitude,
              longitude: place.location.longitude
            }
          : undefined
      }
    }
  }

  if (!isEmpty(kreditsSettingsPayload?.amount)) {
    mutationInput.amount = {
      id: oldValues.businessClient.id,
      customerID: oldCustomer.id,
      amount: sanitizePrice(Number(kreditsSettingsPayload.amount) * 100)
    }
  }

  if (!isEmpty(kreditsSettingsPayload?.category)) {
    mutationInput.category = {
      id: oldCustomer.id,
      category: parseInt(kreditsSettingsPayload?.category as unknown as string)
    }
  }

  if (!isEmpty(payload.businessClient?.id)) {
    mutationInput.businessClient = {
      id: oldCustomer.id,
      businessClientID: formValues.businessClient.id,
      businessSiteID: formValues.businessSite.id || null
    }
  } else if (!isEmpty(payload.businessSite?.id)) {
    mutationInput.businessSite = {
      id: oldCustomer.id,
      businessSiteID: formValues.businessSite.id
    }
  }

  return mutationInput
}

const mapFormToMutation = (formValues: IBusinessCustomerFormValues): GTCreateBusinessCustomerInput => {
  const mutationValues: GTCreateBusinessCustomerInput = {
    businessClientID: formValues.businessClient.id,
    businessSiteID: formValues.businessSite.id || null,
    email: formValues.email || undefined,
    firstName: formValues.firstName || undefined,
    lastName: formValues.lastName || undefined,
    phoneNumber: formValues.phoneNumber.number
      ? {
          number: formValues.phoneNumber.number,
          countryCode: formValues.phoneNumber.countryCode as string
        }
      : undefined,
    type: formValues.customerType
  }
  if (formValues.placeAddress.cityId) {
    mutationValues.place = {
      address: formValues.placeAddress.street,
      zipCode: formValues.placeAddress.zipCode,
      parentID: formValues.placeAddress.cityId,
      location: formValues.placeAddress.location.latitude
        ? {
            latitude: formValues.placeAddress.location.latitude,
            longitude: formValues.placeAddress.location.longitude
          }
        : undefined
    }
  }

  if (formValues.kreditsSettings?.category) {
    mutationValues.kreditCategory = parseInt(formValues.kreditsSettings.category)
  }
  return mutationValues
}

const mapCSVFormToMutation = (formValues: IBusinessCustomerCSVFormValues): GTCreateBusinessCustomersFromCsvInput => {
  const mutationValues: GTCreateBusinessCustomersFromCsvInput = {
    businessClientID: formValues.businessClient.id,
    businessSiteID: formValues.businessSite.id || null,
    type: formValues.customerType as GTBusinessCustomerTypeInput,
    path: formValues.filePath
  }
  return mutationValues
}

const mapResponseToForm = (queryResponse: GTBusinessCustomer): IBusinessCustomerFormValues => {
  const formValues: IBusinessCustomerFormValues = {
    userAccountId: queryResponse.userAccount.id,
    firstName: queryResponse.userAccount.firstName || '',
    lastName: queryResponse.userAccount.lastName || '',
    kreditsSettings: {
      amount: '',
      category: ''
    },
    phoneNumber: queryResponse.userAccount.phoneNumber?.number
      ? {
          number: queryResponse.userAccount.phoneNumber.number,
          countryCode: queryResponse.userAccount.phoneNumber.countryCode
        }
      : {
          number: '',
          countryCode: DEFAULT_COUNTRY_CODE
        },
    email: queryResponse.userAccount.email || '',
    customerType: queryResponse.type as unknown as IBusinessCustomerFormValues['customerType'],
    businessClient: {
      id: queryResponse.businessClient.id,
      name: queryResponse.businessClient.name,
      verticals: queryResponse.businessClient.verticals
    },
    businessSite: {
      id: queryResponse.businessSite?.id || '',
      name: queryResponse.businessSite?.name || ''
    },
    placeAddress: {
      street: queryResponse.place?.address?.line1 || '',
      zipCode: queryResponse.place?.address?.zipCode || '',
      cityName: queryResponse.place?.parent.name || '',
      cityId: queryResponse.place?.parent.id || '',
      location: {
        latitude: '',
        longitude: ''
      }
    }
  }

  return formValues
}

export const mapFiltersToSearchQueryString = (filters: IBusinessCustomersFiltersForm, pageNb = 1): string => {
  return getQueryString({
    status: filters.status,
    phone: filters.phone,
    email: encodeURIComponent(filters.email),
    userId: filters.userId,
    customerId: filters.customerId,
    clientId: filters.client.id,
    siteId: filters.site.id,
    category: filters.category,
    ids: filters.businessCustomers.map((customer) => customer.id).join(','),
    page: pageNb
  })
}

export const mapSearchToFiltersForm = (
  queries: Record<string, string | undefined>,
  {
    businessCustomers,
    businessClient,
    businessSite
  }: {
    businessCustomers: BusinessCustomerSuggestion[]
    businessClient: BusinessClientSuggestion | null
    businessSite: BusinessSiteSuggestion | null
  }
): IBusinessCustomersFiltersForm => {
  const businessCustomersIds = queries.ids ? queries.ids?.split(',') : []

  return {
    status: queries.status || '',
    phone: queries.phone || '',
    email: queries.email || '',
    userId: queries.userId || '',
    customerId: queries.customerId || '',
    category: queries.category ?? '',
    site: businessSite || {
      id: queries.siteId || '',
      name: ''
    },
    client: businessClient || {
      id: queries.clientId || '',
      name: ''
    },
    businessCustomers: !isEmpty(businessCustomers)
      ? businessCustomers
      : businessCustomersIds.map((id) => ({ id, name: '' }))
  }
}

export const mapFiltersToQueryVariables = (filters: IBusinessCustomersFiltersForm): GTBusinessCustomersInput => {
  return {
    businessClientID: filters.client.id || undefined,
    businessCustomerID: filters.customerId || undefined,
    businessSiteID: filters.site.id || undefined,
    ids: filters.businessCustomers.map((customer) => customer.id),
    category: !isUndefined(filters.category) ? parseInt(filters.category) : undefined,
    email: filters.email || undefined,
    isEnabled:
      filters.status === EBusinessCustomerStatus.ACTIVE
        ? true
        : filters.status === EBusinessCustomerStatus.INACTIVE
        ? false
        : undefined,
    // @ts-expect-error TODO: Fix this when the backend is ready
    phoneNumber: filters.phone
      ? {
          number: filters.phone || undefined
        }
      : undefined,
    userAccountID: filters.userId || undefined
  }
}

export const mapBusinessCustomerUserAccountToPdf = (
  qrCodeData: string,
  userAccount: GTBusinessCustomer['userAccount']
): IBusinessCustomerUserAccountPDFData => {
  return {
    id: userAccount.id.substring(0, 8),
    phone:
      (userAccount.phoneNumber?.countryCode ? `+${userAccount.phoneNumber?.countryCode} ` : '') +
      (userAccount.phoneNumber?.number || ''),
    email: userAccount.email,
    name: `${userAccount.firstName ? userAccount.firstName + ' ' : ''}${userAccount.lastName ?? ''}`,
    qrCode: qrCodeData,
    qrImageBase64: ''
  }
}

export const mapBusinessCustomerUserAccountToQR = (userAccount: IBusinessCustomerUserAccountPDFData): string => {
  return userAccount.qrCode
}

export const BusinessCustomerFormUtils = {
  mapFormToUpdateMutation,
  mapFormToMutation,
  mapCSVFormToMutation,
  mapResponseToForm,
  mapSearchToFiltersForm
}

export const getCSVErrors = (error: ApolloError | undefined): TSFixMe[] => {
  const errors = error?.graphQLErrors?.[0]?.extensions?.errors ?? []
  return errors.filter((error: { field: string }) => error.field.includes('Records'))
}

export const hasCSVError = (error: ApolloError | undefined): boolean => {
  return !isEmpty(getCSVErrors(error))
}

export const getCSVBusinessCustomerErrors = (error: ApolloError | undefined, intl: IntlShape): string => {
  try {
    const errorMessages = getCSVErrors(error).map((item: { field: string }) => {
      const rowNumberStr = item.field.match(/(\d+)/)?.[0]
      const adjustedRowNumberStr = rowNumberStr ? String(parseInt(rowNumberStr) + 2) : ''
      let errorMsg = item.field.split('.')[1] ?? ''

      if (errorMsg === 'parent') {
        errorMsg = intl.formatMessage(csvErrorMessages.invalidCity)
      }

      return `${adjustedRowNumberStr} (${errorMsg})`
    })
    if (errorMessages.length > 0) {
      return intl.formatMessage(csvErrorMessages.rowError, { content: errorMessages.join(', ') })
    }
    return intl.formatMessage(csvErrorMessages.genericError)
  } catch (error) {
    return intl.formatMessage(csvErrorMessages.genericError)
  }
}
